import { ReactNode, useEffect, useReducer, useState } from "react";
import { clsx } from "clsx";
import * as Popover from "@radix-ui/react-popover";
import Search from "../Search";
import { useDispatch, useSelector } from "react-redux";
import { setFilterValues } from "../../store/reducers/listingReducer";
import { baseQueryAPI } from "../../services/baseQuery";
import { useLocation } from "react-router-dom";

const initialState = {
  built: null,
  sourceType: "",
  timeListed: "",
  leadStatus: [],
  bedrooms: 0,
  bathrooms: 0,
  price_drop: [],
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case "SET_BUILT":
      return { ...state, built: action.payload };
    case "SET_SOURCE_TYPE":
      return { ...state, sourceType: action.payload };
    case "SET_TIME_LISTED":
      return { ...state, timeListed: action.payload };
    case "SET_LEAD_STATUS":
      return { ...state, leadStatus: action.payload };
    case "SET_BEDROOMS":
      return { ...state, bedrooms: action.payload };
    case "SET_BATHROOMS":
      return { ...state, bathrooms: action.payload };
    case "SET_PRICE_DROP":
      return { ...state, price_drop: action.payload };
    case "INITIALIZE_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const initializeState = (userFilter: any) => {
  if (!userFilter?.data) return initialState;

  return {
    built: userFilter.data.year_built
      ? {
          start: userFilter.data.year_built[0],
          end: userFilter.data.year_built[1],
        }
      : null,
    sourceType: userFilter.data.source_type || "",
    timeListed: userFilter.data.time_listed || "",
    leadStatus:
      userFilter.data.lead_status && userFilter.data.lead_status[0]
        ? userFilter.data.lead_status
        : [],
    bedrooms: Number(userFilter.data.bedrooms) || 0,
    bathrooms: Number(userFilter.data.bathrooms) || 0,
    price_drop: userFilter.data.price_drop || [],
  };
};

export const FilterListing = () => {
  const dispatch = useDispatch();
  const { data: userFilter } = baseQueryAPI.useUserFilterQuery("user-filter");
  const appType = useSelector((state: any) => state.listingReducer.appType);
  /*const appType = useSelector((state: any) => state.listingReducer.appType);

    const { data: userFilter } = baseQueryAPI.useUserFilterQuery("user-filter", {
      skip: appType === "investment" || appType === "commercial",
    });*/

  const [state, dispatchLocal] = useReducer(reducer, initialState, () =>
    initializeState(userFilter)
  );

  const isCommercial = appType === "commercial";

  const {
    built,
    sourceType,
    timeListed,
    leadStatus,
    bedrooms,
    bathrooms,
    price_drop,
  } = state;

  const [updateFilter] = baseQueryAPI.useUpdateUserFilterMutation();

  console.log({ appType });

  useEffect(() => {
    if (userFilter?.data) {
      dispatchLocal({
        type: "INITIALIZE_STATE",
        payload: initializeState(userFilter),
      });
    }
  }, [userFilter]);

  const saveFilter = async (apiPatch = {}) => {
    const payload = {
      lead_status: leadStatus,
      time_listed: timeListed ? timeListed : null,
      equity_percent: null,
      year_built: built ? [built.start.toString(), built.end.toString()] : null,
      source_type: sourceType,
      bedrooms: bedrooms.toString(),
      bathrooms: bathrooms.toString(),
      price_drop,
      ...apiPatch,
    };

    updateFilter(payload);
  };

  const leadStatusOptions = useSelector(
    (state: any) => state.listingReducer.leadStatusOptions
  );

  const filterValues = useSelector(
    (state: any) => state.listingReducer.filterValues
  );

  const onFilterApply = async (reduxPatch = {}, apiPatch = {}) => {
    applyFilter({
      filterBounds: filterValues.filterBounds,
      filterTime: timeListed,
      filterLeadStatus: leadStatus[0] || "",
      filterBuiltYear: built,
      filterBathrooms: bathrooms,
      filterBedrooms: bedrooms,
      filterBySourceType: sourceType,
      filterByPriceDrop: price_drop,
      ...reduxPatch,
    });

    if (!isCommercial) {
      await saveFilter(apiPatch);
    }
  };

  const applyFilter = (value: any) => {
    dispatch(setFilterValues(value));
  };

  const getSelectedRoomsVal = () => {
    if (bedrooms && bathrooms) {
      return `${bedrooms} bed - ${bathrooms} bath`;
    }

    if (bedrooms === 0 && bathrooms !== 0) {
      return `${bathrooms} bath`;
    }

    if (bathrooms === 0 && bedrooms !== 0) {
      return `${bedrooms} bed`;
    }

    return "";
  };

  return (
    <div className="flex items-baseline gap-x-3 p-3 h-full">
      <div className="h-full">
        <Search />
      </div>

      {appType !== "commercial" ? (
        <>
          <Filter
            selected={getSelectedRoomsVal()}
            name="Rooms"
            clearFilter={() => {
              dispatchLocal({ type: "SET_BEDROOMS", payload: 0 });
              dispatchLocal({ type: "SET_BATHROOMS", payload: 0 });
              onFilterApply(
                { filterBedrooms: 0, filterBathrooms: 0 },
                { bedrooms: "0", bathrooms: "0" }
              );
            }}
          >
            <RoomsFilter
              val={{ bedrooms, bathrooms }}
              setVal={(val) => {
                dispatchLocal({ type: "SET_BEDROOMS", payload: val.bedrooms });
                dispatchLocal({
                  type: "SET_BATHROOMS",
                  payload: val.bathrooms,
                });
                onFilterApply(
                  {
                    filterBedrooms: val.bedrooms,
                    filterBathrooms: val.bathrooms,
                  },
                  {
                    bedrooms: val.bedrooms.toString(),
                    bathrooms: val.bathrooms.toString(),
                  }
                );
              }}
            />
          </Filter>
          <Filter
            selected={built ? `${built.start} - ${built.end}` : ""}
            name="Year Built"
            clearFilter={() => {
              dispatchLocal({ type: "SET_BUILT", payload: null });
              onFilterApply({ filterBuiltYear: null }, { year_built: null });
            }}
          >
            <YearFilter
              built={built}
              setBuilt={(val) => {
                dispatchLocal({ type: "SET_BUILT", payload: val });
                onFilterApply(
                  { filterBuiltYear: val },
                  {
                    year_built: val
                      ? [val.start.toString(), val.end.toString()]
                      : null,
                  }
                );
              }}
            />
          </Filter>
          <Filter
            selected={sourceType}
            name="Source Type"
            clearFilter={() => {
              dispatchLocal({ type: "SET_SOURCE_TYPE", payload: "" });
              onFilterApply({ filterBySourceType: "" }, { source_type: "" });
            }}
          >
            <SourceFilter
              setSourceType={(val) => {
                dispatchLocal({ type: "SET_SOURCE_TYPE", payload: val });
                onFilterApply(
                  { filterBySourceType: val },
                  { source_type: val }
                );
              }}
              sourceType={sourceType}
            />
          </Filter>
        </>
      ) : null}
      <Filter
        selected={
          leadStatus.length > 0 ? `Lead Status (${leadStatus.length})` : ""
        }
        name="Lead Status"
        clearFilter={() => {
          dispatchLocal({ type: "SET_LEAD_STATUS", payload: [] });
          onFilterApply({ filterLeadStatus: "" }, { lead_status: [] });
        }}
      >
        <LeadsFilter
          leadStatus={leadStatus}
          leadStatusOptions={leadStatusOptions}
          setLeadStatus={(val) => {
            dispatchLocal({ type: "SET_LEAD_STATUS", payload: val });
            onFilterApply(
              { filterLeadStatus: val[0] || "" },
              { lead_status: val }
            );
          }}
        />
      </Filter>
      <Filter
        selected={
          timeListed
            ? timeListed === "180"
              ? "6 Months"
              : `${timeListed} days`
            : ""
        }
        name="Time Listed"
        clearFilter={() => {
          dispatchLocal({ type: "SET_TIME_LISTED", payload: "" });
          onFilterApply({ filterTime: "" }, { time_listed: "" });
        }}
      >
        <TimeListedFilter
          setTimeListed={(val) => {
            dispatchLocal({ type: "SET_TIME_LISTED", payload: val });
            onFilterApply({ filterTime: val }, { time_listed: val });
          }}
          timeListed={timeListed}
        />
      </Filter>
      {/*{appType === "residential" ? (

      ) : null}*/}

      <Filter
        selected={
          price_drop.length > 0 ? `Price Drop (${price_drop.length})` : ""
        }
        name="Price Drop"
        clearFilter={() => {
          dispatchLocal({ type: "SET_PRICE_DROP", payload: [] });
          onFilterApply({ filterByPriceDrop: [] }, { price_drop: [] });
        }}
      >
        <PriceDropFilter
          priceDrop={price_drop}
          setPriceDrop={(val) => {
            dispatchLocal({ type: "SET_PRICE_DROP", payload: val });
            onFilterApply({ filterByPriceDrop: val }, { price_drop: val });
          }}
          isCommercial={appType === "commercial"}
        />
      </Filter>

      {/* <button
        onClick={onFilterApply}
        className="bg-[#0077FF] px-4 py-3 text-white font-medium hidden md:block"
      >
        Save Search
      </button> */}
    </div>
  );
};

const Filter = ({
  name,
  selected,
  children,
  clearFilter,
}: {
  name: string;
  selected: string;
  children: ReactNode;
  clearFilter: () => void;
}) => {
  return (
    <Popover.Root modal={true}>
      <Popover.Anchor>
        <div
          className={clsx(
            "flex gap-x-2 py-3 px-4 items-center border shadow-sm",
            selected && "bg-[#EFF4FF] border-[#84ADFF]"
          )}
        >
          <Popover.Trigger>
            <button className="flex gap-x-1 text-gray-700 font-semibold h-full">
              {selected ? <span>{selected}</span> : <span>{name}</span>}

              {!selected ? (
                <span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#344054"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              ) : null}
            </button>
          </Popover.Trigger>
          {selected ? (
            <button onClick={clearFilter}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="#344054"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          ) : null}
        </div>
      </Popover.Anchor>
      <Popover.Portal>{children}</Popover.Portal>
    </Popover.Root>
  );
};

const RoomsFilter = ({
  val,
  setVal,
}: {
  val: any;
  setVal: (val: any) => void;
}) => {
  const [bedrooms, setBedrooms] = useState(val.bedrooms);
  const [bathrooms, setBathrooms] = useState(val.bathrooms);
  useEffect(() => {
    setBathrooms(val.bathrooms);
    setBedrooms(val.bedrooms);
  }, [val]);
  return (
    <Popover.Content
      onOpenAutoFocus={(e) => e.preventDefault()}
      onFocusOutside={(e) => e.preventDefault()}
      align="start"
      className="bg-white py-4 px-5 w-[24.8rem] border shadow-sm mt-2"
    >
      <div>
        <div>
          <h4 className="text-sm font-semibold text-gray-700">
            Number of Bedrooms
          </h4>
          <div className="flex mt-3">
            <button
              onClick={() => setBedrooms(0)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bedrooms === 0 ? "bg-blue-200" : ""
              }`}
            >
              Any
            </button>
            <button
              onClick={() => setBedrooms(1)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bedrooms === 1 ? "bg-blue-200" : ""
              }`}
            >
              1+
            </button>
            <button
              onClick={() => setBedrooms(2)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bedrooms === 2 ? "bg-blue-200" : ""
              }`}
            >
              2+
            </button>
            <button
              onClick={() => setBedrooms(3)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bedrooms === 3 ? "bg-blue-200" : ""
              }`}
            >
              3+
            </button>
            <button
              onClick={() => setBedrooms(4)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bedrooms === 4 ? "bg-blue-200" : ""
              }`}
            >
              4+
            </button>
            <button
              onClick={() => setBedrooms(5)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bedrooms === 5 ? "bg-blue-200" : ""
              }`}
            >
              5+
            </button>
          </div>
        </div>

        <div className="mt-3">
          <h4 className="text-sm font-semibold text-gray-700">
            Number of Bathrooms
          </h4>
          <div className="flex mt-3">
            <button
              onClick={() => setBathrooms(0)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bathrooms === 0 ? "bg-blue-200" : ""
              }`}
            >
              Any
            </button>
            <button
              onClick={() => setBathrooms(1)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bathrooms === 1 ? "bg-blue-200" : ""
              }`}
            >
              1+
            </button>
            <button
              onClick={() => setBathrooms(2)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bathrooms === 2 ? "bg-blue-200" : ""
              }`}
            >
              2+
            </button>
            <button
              onClick={() => setBathrooms(3)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bathrooms === 3 ? "bg-blue-200" : ""
              }`}
            >
              3+
            </button>
            <button
              onClick={() => setBathrooms(4)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bathrooms === 4 ? "bg-blue-200" : ""
              }`}
            >
              4+
            </button>
            <button
              onClick={() => setBathrooms(5)}
              className={`flex-1 border border-gray-300 py-3 font-semibold text-sm text-gray-800 ${
                bathrooms === 5 ? "bg-blue-200" : ""
              }`}
            >
              5+
            </button>
          </div>
        </div>

        <div className="w-full mt-3">
          <Popover.Close asChild>
            <button
              onClick={() => setVal({ bedrooms, bathrooms })}
              className="bg-[#0077FF] text-white font-semibold py-2 w-full"
            >
              Apply
            </button>
          </Popover.Close>
        </div>
      </div>
    </Popover.Content>
  );
};

const YearFilter = ({
  built: val,
  setBuilt: setVal,
}: {
  built: {
    start: number;
    end: number;
  } | null;
  setBuilt: (val: any) => void;
}) => {
  const [built, setBuilt] = useState<{
    start: number;
    end: number;
  } | null>(val);
  useEffect(() => {
    setBuilt(val);
  }, [val]);
  return (
    <Popover.Content
      align="start"
      className="bg-white py-4 px-5 border shadow-sm mt-2"
    >
      <div>
        <h4 className="text-sm font-semibold text-gray-700">Year Built</h4>

        <div className="flex gap-x-3 items-center mt-3">
          <div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700">Minimum</h4>
            </div>
            <YearInput
              val={built?.start || 0}
              setVal={(val) =>
                setBuilt(
                  built ? { ...built, start: val } : { end: 0, start: val }
                )
              }
            />
          </div>

          <div className="mt-6">-</div>
          <div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700">Maximum</h4>
            </div>
            <YearInput
              val={built?.end || 0}
              setVal={(val) =>
                setBuilt(
                  built ? { ...built, end: val } : { start: 0, end: val }
                )
              }
            />
          </div>
        </div>

        <div className="w-full mt-3">
          <Popover.Close asChild>
            <button
              onClick={() => setVal(built)}
              className="bg-[#0077FF] text-white font-semibold py-2 w-full"
            >
              Apply
            </button>
          </Popover.Close>
        </div>
      </div>
    </Popover.Content>
  );
};

const YearInput = ({
  val,
  setVal,
}: {
  val: any;
  setVal: (val: any) => void;
}) => {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <div className="relative mt-2">
          <input
            defaultChecked={val}
            type="number"
            min={1990}
            max={2023}
            onChange={(e) => setVal(Number(e.target.value))}
            className="border py-2 px-3 w-[10.31rem] outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <div className="w-fit absolute top-1/4 right-3">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6L8 10L12 6"
                stroke="black"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </Popover.Trigger>

      <Popover.Content className="bg-white max-h-[300px] w-[10.31rem] overflow-y-auto border mt-3 outline-none">
        {Array.from({ length: 2024 - 1990 + 1 }, (_, i) => i + 1990).map(
          (year) => (
            <Popover.Close asChild>
              <button
                onClick={() => setVal(year)}
                className={clsx(
                  "px-4 py-2 text-gray-950 hover:bg-gray-200 cursor-pointer w-full text-start outline-none",
                  val === year && "text-[#0077FF] border-l-2 border-l-[#0077FF]"
                )}
              >
                {year}
              </button>
            </Popover.Close>
          )
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

const SourceFilter = ({
  sourceType: val,
  setSourceType: setVal,
}: {
  sourceType: string;
  setSourceType: (val: string) => void;
}) => {
  const [sourceType, setSourceType] = useState(val);
  useEffect(() => {
    setSourceType(val);
  }, [val]);
  return (
    <Popover.Content
      align="start"
      className="bg-white py-4 px-5 border shadow-sm mt-2"
    >
      <div>
        <h4 className="text-sm font-semibold text-gray-700">Source Type</h4>

        <div className=" mt-3">
          <Radio
            onChange={(e) => setSourceType("")}
            selected={sourceType === ""}
            id="any"
            label="Any"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Notice of Trustee Sale")}
            selected={sourceType === "Notice of Trustee Sale"}
            label="Notice of Trustee Sale"
            id="Notice of Trustee Sale"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Lis Penden")}
            selected={sourceType === "Lis Penden"}
            id="Lis Penden"
            label="Lis Penden"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Code Violation")}
            selected={sourceType === "Code Violation"}
            id="Code Violation"
            label="Code Violation"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Death Certificate")}
            selected={sourceType === "Death Certificate"}
            id="Death Certificate"
            label="Death Certificate"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("C. Notice of Trustee Sale")}
            selected={sourceType === "C. Notice of Trustee Sale"}
            id="C. Notice of Trustee Sale"
            label="C. Notice of Trustee Sale"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Lien")}
            selected={sourceType === "Lien"}
            id="Lien"
            label="Lien"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Judgement")}
            selected={sourceType === "Judgement"}
            id="Judgement"
            label="Judgement"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Divorce")}
            selected={sourceType === "Divorce"}
            id="Divorce"
            label="Divorce"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Probate")}
            selected={sourceType === "Probate"}
            id="Probate"
            label="Probate"
            name="source_type"
          />
          <Radio
            onChange={(e) => setSourceType("Notice of Default")}
            selected={sourceType === "Notice of Default"}
            id="Notice of Default"
            label="Notice of Default"
            name="source_type"
          />
        </div>

        <div className="w-full mt-3">
          <Popover.Close asChild>
            <button
              onClick={() => setVal(sourceType)}
              className="bg-[#0077FF] text-white font-semibold py-2 w-full"
            >
              Apply
            </button>
          </Popover.Close>
        </div>
      </div>
    </Popover.Content>
  );
};

const TimeListedFilter = ({
  timeListed: val,
  setTimeListed: setVal,
}: {
  timeListed: string;
  setTimeListed: (val: string) => void;
}) => {
  const [timeListed, setTimeListed] = useState(val);
  useEffect(() => {
    setTimeListed(val);
  }, [val]);
  return (
    <Popover.Content
      align="start"
      className="bg-white py-4 px-5 border shadow-sm mt-2"
    >
      <div>
        <h4 className="text-sm font-semibold text-gray-700">Time Listed</h4>

        <div className="mt-3">
          <Radio
            onChange={(e) => setTimeListed("")}
            selected={timeListed === ""}
            id="any"
            label="Any"
            name="time"
          />
          <Radio
            onChange={(e) => setTimeListed("1")}
            selected={timeListed === "1"}
            id="1day"
            label="1 Day"
            name="time"
          />
          <Radio
            onChange={(e) => setTimeListed("7")}
            selected={timeListed === "7"}
            id="7days"
            label="7 Days"
            name="time"
          />
          <Radio
            onChange={(e) => setTimeListed("14")}
            selected={timeListed === "14"}
            id="14days"
            label="14 Days"
            name="time"
          />
          <Radio
            onChange={(e) => setTimeListed("30")}
            selected={timeListed === "30"}
            id="30days"
            label="30 Days"
            name="time"
          />
          <Radio
            onChange={(e) => setTimeListed("90")}
            selected={timeListed === "90"}
            id="90days"
            label="90 Days"
            name="time"
          />
          <Radio
            onChange={(e) => setTimeListed("180")}
            selected={timeListed === "180"}
            id="6months"
            label="6 Months"
            name="time"
          />
        </div>

        <div className="w-full mt-3">
          <Popover.Close asChild>
            <button
              onClick={() => setVal(timeListed)}
              className="bg-[#0077FF] text-white font-semibold py-2 w-full"
            >
              Apply
            </button>
          </Popover.Close>
        </div>
      </div>
    </Popover.Content>
  );
};

const LeadsFilter = ({
  leadStatus: val,
  leadStatusOptions,
  setLeadStatus: setVal,
}: {
  leadStatus: string[];
  leadStatusOptions: any;
  setLeadStatus: (val: string[]) => void;
}) => {
  const [leadStatus, setLeadStatus] = useState<string[]>(val);
  useEffect(() => {
    setLeadStatus(val);
  }, [val]);
  const onLeadStatusChange = (value: string, add: boolean) => {
    if (add) {
      setLeadStatus((state) => [...state, value]);
    } else {
      setLeadStatus((state) => state.filter((itm) => itm !== value));
    }
  };

  const selectAll = () => {
    setLeadStatus(leadStatusOptions.map((itm: any) => itm.public_id));
  };

  return (
    <Popover.Content
      align="start"
      className="bg-white py-4 px-5 border shadow-sm mt-2"
    >
      <div className="pr-3 w-56">
        <div className="flex justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Lead Status</h4>

          {leadStatusOptions.length === leadStatus.length ? (
            <button
              onClick={() => setLeadStatus([])}
              className="text-sm font-medium text-primary"
            >
              Clear
            </button>
          ) : (
            <button
              onClick={selectAll}
              className="text-sm font-medium text-primary"
            >
              Select All
            </button>
          )}
        </div>

        <ul className="mt-3">
          <>
            {leadStatusOptions.map((option: any) => (
              <li
                className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
                key={"listing-filter-" + option.public_id}
              >
                <input
                  type="checkbox"
                  name={option.option_name}
                  id={option.public_id}
                  onChange={(e) =>
                    onLeadStatusChange(option.public_id, e.target.checked)
                  }
                  checked={leadStatus.includes(option.public_id)}
                />
                <label htmlFor={option.public_id}>{option.option_name}</label>
              </li>
            ))}
          </>
        </ul>

        <div className="w-full mt-3">
          <Popover.Close asChild>
            <button
              onClick={() => setVal(leadStatus)}
              className="bg-[#0077FF] text-white font-semibold py-2 w-full"
            >
              Apply
            </button>
          </Popover.Close>
        </div>
      </div>
    </Popover.Content>
  );
};

const PriceDropFilter = ({
  priceDrop: val,
  setPriceDrop: setVal,
  isCommercial,
}: {
  priceDrop: number[];
  setPriceDrop: (val: number[]) => void;
  isCommercial: boolean;
}) => {
  const [leadStatus, setLeadStatus] = useState<number[]>(val);
  useEffect(() => {
    setLeadStatus(val);
  }, [val]);
  const onLeadStatusChange = (value: number, add: boolean) => {
    if (add) {
      setLeadStatus((state) => [...state, value]);
    } else {
      setLeadStatus((state) => state.filter((itm) => itm !== value));
    }
  };

  return (
    <Popover.Content
      align="start"
      className="bg-white py-4 px-5 border shadow-sm mt-2"
    >
      <div className="pr-3 w-56">
        <div className="flex justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Price Drop</h4>
        </div>

        {isCommercial ? (
          <CommercialPriceDrop
            leadStatus={leadStatus}
            onLeadStatusChange={onLeadStatusChange}
          />
        ) : (
          <NonCommercialPriceDrop
            onLeadStatusChange={onLeadStatusChange}
            leadStatus={leadStatus}
          />
        )}

        <div className="w-full mt-3">
          <Popover.Close asChild>
            <button
              onClick={() => setVal(leadStatus)}
              className="bg-[#0077FF] text-white font-semibold py-2 w-full"
            >
              Apply
            </button>
          </Popover.Close>
        </div>
      </div>
    </Popover.Content>
  );
};

const Radio = ({
  id,
  name,
  label,
  selected,
  onChange,
}: {
  id: string;
  name: string;
  label: string;
  selected: boolean;
  onChange: (val: string) => void;
}) => {
  return (
    <div className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700">
      <input
        checked={selected}
        onChange={() => onChange(id)}
        type="radio"
        name={name}
        id={id}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
};

const CommercialPriceDrop = ({
  leadStatus,
  onLeadStatusChange,
}: {
  leadStatus: number[];
  onLeadStatusChange: (value: number, add: boolean) => void;
}) => {
  return (
    <ul className="mt-3">
      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="new-lead"
      >
        <input
          type="checkbox"
          name="new"
          id="new"
          onChange={(e) => onLeadStatusChange(0, e.target.checked)}
          checked={leadStatus.includes(0)}
        />
        <label htmlFor="new">New Lead</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="first-drop"
      >
        <input
          type="checkbox"
          name="first-drop"
          id="first-drop"
          onChange={(e) => onLeadStatusChange(1, e.target.checked)}
          checked={leadStatus.includes(1)}
        />
        <label htmlFor="first-drop">First Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="second-drop"
      >
        <input
          type="checkbox"
          name="second-drop"
          id="second-drop"
          onChange={(e) => onLeadStatusChange(2, e.target.checked)}
          checked={leadStatus.includes(2)}
        />
        <label htmlFor="second-drop">Second Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="third-drop"
      >
        <input
          type="checkbox"
          name="third-drop"
          id="third-drop"
          onChange={(e) => onLeadStatusChange(3, e.target.checked)}
          checked={leadStatus.includes(3)}
        />
        <label htmlFor="third-drop">Third Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="fourth-drop"
      >
        <input
          type="checkbox"
          name="fourth-drop"
          id="fourth-drop"
          onChange={(e) => onLeadStatusChange(4, e.target.checked)}
          checked={leadStatus.includes(4)}
        />
        <label htmlFor="fourth-drop">Fourth Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="fifth-drop"
      >
        <input
          type="checkbox"
          name="fifth-drop"
          id="fifth-drop"
          onChange={(e) => onLeadStatusChange(5, e.target.checked)}
          checked={leadStatus.includes(5)}
        />
        <label htmlFor="fifth-drop">Fifth Drop</label>
      </li>
    </ul>
  );
};

const NonCommercialPriceDrop = ({
  leadStatus,
  onLeadStatusChange,
}: {
  leadStatus: number[];
  onLeadStatusChange: (value: number, add: boolean) => void;
}) => {
  return (
    <ul className="mt-3">
      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="fourth-drop"
      >
        <input
          type="checkbox"
          name="fourth-drop"
          id="fourth-drop"
          onChange={(e) => onLeadStatusChange(4, e.target.checked)}
          checked={leadStatus.includes(4)}
        />
        <label htmlFor="fourth-drop">Fourth Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="fifth-drop"
      >
        <input
          type="checkbox"
          name="fifth-drop"
          id="fifth-drop"
          onChange={(e) => onLeadStatusChange(5, e.target.checked)}
          checked={leadStatus.includes(5)}
        />
        <label htmlFor="fifth-drop">Fifth Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="fifth-drop"
      >
        <input
          type="checkbox"
          name="sixth-drop"
          id="sixth-drop"
          onChange={(e) => onLeadStatusChange(6, e.target.checked)}
          checked={leadStatus.includes(6)}
        />
        <label htmlFor="sixth-drop">Sixth Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="fifth-drop"
      >
        <input
          type="checkbox"
          name="seventh-drop"
          id="seventh-drop"
          onChange={(e) => onLeadStatusChange(7, e.target.checked)}
          checked={leadStatus.includes(7)}
        />
        <label htmlFor="seventh-drop">Seventh Drop</label>
      </li>

      <li
        className="py-2 flex items-center gap-x-2 text-sm font-medium text-gray-700"
        key="fifth-drop"
      >
        <input
          type="checkbox"
          name="eighth-drop"
          id="eighth-drop"
          onChange={(e) => onLeadStatusChange(8, e.target.checked)}
          checked={leadStatus.includes(8)}
        />
        <label htmlFor="eighth-drop">Eighth Drop</label>
      </li>
    </ul>
  );
};
